import {
  TimePicker,
  TimePickerOverrides,
  TimePickerProps,
} from 'baseui/timepicker';
import { format } from 'date-fns';
import * as React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import CustomFormControl, {
  CustomFormControlProps,
} from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';
import ReadOnlyText from '../read-only-text';
import { styles } from '../styles';

export interface TimePickerInputProps
  extends BaseElementInputProps,
    Omit<
      CustomFormControlProps,
      'name' | 'overrides' | 'positive' | 'error' | 'children'
    >,
    Omit<TimePickerProps, 'name' | 'adapter'> {
  type: 'time';
  fieldType?: 'control' | 'uncontrol';
}

const timePickerOverrides: ({
  overrides,
  disabled,
  onBlur,
}) => TimePickerOverrides = ({ overrides, disabled, onBlur }) => ({
  Select: {
    props: {
      onBlur,
      overrides: {
        Root: {
          style: {
            zIndex: 1,
            style: {
              ...(disabled ? styles.reset : styles.active),
            },
          },
        },
        ControlContainer: {
          style: {
            zIndex: 201,
            ...(disabled ? styles.reset : styles.active),
          },
        },
        Popover: {
          props: {
            overrides: {
              Body: {
                style: {
                  zIndex: 201,
                },
              },
            },
          },
        },
        ValueContainer: {
          style: {
            paddingTop: '2px',
            paddingBottom: '2px',
          },
        },
      },
    },
  },
  ...overrides,
});

export default function TimePickerInput(props: TimePickerInputProps) {
  const { control } = useFormContext<any>();
  const watchValue = useWatch({ control, name: props.name });
  const { field, fieldState } = useController({ name: props.name });
  const context = React.useContext(FormContext);
  const {
    readOnly,
    step = 1800,
    hideLabel,
    label = '',
    overrides,
    fieldType = 'control',
    format: formatTime = '24',
    ...restProps
  } = props;

  const disabled = !context.editable || restProps.disabled;

  const _readOnlyValue = React.useMemo(() => {
    if (watchValue) {
      return format(watchValue, 'HH:mm');
    }

    return undefined;
  }, [watchValue]);

  if (readOnly) {
    return (
      <ReadOnlyText
        hideLabel={hideLabel}
        label={label}
        value={_readOnlyValue}
      />
    );
  }
  const shouldShowError = !!fieldState.error?.message;

  return (
    <CustomFormControl
      {...restProps}
      label={label}
      hideLabel={hideLabel}
      error={fieldState.error?.message}
    >
      <TimePicker
        {...restProps}
        {...field}
        {...(fieldType === 'uncontrol' && {
          value: watchValue,
        })}
        format={formatTime}
        step={step}
        error={shouldShowError}
        disabled={disabled}
        overrides={timePickerOverrides({
          overrides: props.overrides,
          disabled,
          onBlur: field.onBlur,
        })}
      />
    </CustomFormControl>
  );
}
